// Medicare Workspace — top-level container with 4 panels per the
// architecture diagram: AEP Command Center, T65 Pipeline, SOA Tracker,
// SEP Trigger Engine. Plan Comparison + Disenrollment Alerts deferred.
//
// Pulls everything in one round trip (/api/medicare/dashboard), writes
// through /api/medicare/actions. Pure additive — no existing tables
// or views mutated.

import React from 'react';
import { readPersistedJwtDirect } from '../lib/authedFetch';
import { AlertTriangle, Calendar, Send, Megaphone, Plus, ChevronRight, ShieldCheck, Clock } from 'lucide-react';
import { supabase } from '../lib/supabase';
import IrmaaCalculator from './IrmaaCalculator';
import MedigapBirthdayRule from './MedigapBirthdayRule';

async function apiFetch(path, opts = {}) {
  const jwt = readPersistedJwtDirect();
  const res = await fetch(path, {
    ...opts,
    headers: { 'content-type': 'application/json', ...(jwt ? { Authorization: `Bearer ${jwt}` } : {}), ...(opts.headers || {}) },
  });
  const body = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(body?.error || `HTTP ${res.status}`);
  return body;
}

const T65_STAGES = [
  { id: 'cold_start',    label: 'Cold Start',     tone: 'bg-slate-50 border-slate-200' },
  { id: 'pre_65',        label: 'Pre-65',         tone: 'bg-sky-50 border-sky-200' },
  { id: 't65_eligible',  label: 'T65 Eligible',   tone: 'bg-amber-50 border-amber-200' },
  { id: 'quoted',        label: 'Quoted',         tone: 'bg-violet-50 border-violet-200' },
  { id: 'enrolled',      label: 'Enrolled',       tone: 'bg-emerald-50 border-emerald-200' },
];

const SEP_KINDS = [
  { id: 'birthday',                label: 'MA Birthday Rule' },
  { id: 'lis_change',              label: 'LIS / Extra Help change' },
  { id: 'move',                    label: 'Moved out of service area' },
  { id: 'lost_employer_coverage',  label: 'Lost employer coverage' },
];

const CAMPAIGN_KINDS = [
  { id: 'aep_renewal_at_risk',         label: 'AEP — Renewal at risk' },
  { id: 'aep_plan_change_available',   label: 'AEP — Plan change available' },
  { id: 'aep_general_touch',           label: 'AEP — General touch' },
  { id: 't65_birthday',                label: 'T65 Birthday' },
  { id: 'sep_birthday',                label: 'SEP — Birthday' },
];

export default function MedicareWorkspaceView({ currentUser, showToast = () => {} }) {
  const [data, setData]       = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [error, setError]     = React.useState(null);

  const load = React.useCallback(async () => {
    setLoading(true); setError(null);
    try { setData(await apiFetch('/api/medicare/dashboard')); }
    catch (err) { setError(String(err.message || err)); }
    finally { setLoading(false); }
  }, []);
  React.useEffect(() => { load(); }, [load]);

  if (loading) return <div className="text-sm text-slate-500 p-4">Loading Medicare workspace…</div>;
  if (error)   return <div className="bg-rose-50 border border-rose-200 rounded-xl p-4 text-sm text-rose-800">{error}</div>;
  if (!data)   return null;

  return (
    <div className="space-y-5">
      <AEPCommandCenter data={data} onReload={load} showToast={showToast}/>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        <SEPTriggerEngine data={data} onReload={load} showToast={showToast}/>
        <RenewalsAtRiskPanel renewals={data.renewals}/>
      </div>
      <T65PipelinePanel t65={data.t65} onReload={load} showToast={showToast}/>
      {/* IRMAA estimator — Medicare beneficiaries in tier 2+ need this
          conversation regularly (Form SSA-44 appeals on life-changing
          events). Per audit response §18 Phase 9 Medicare row "IRMAA
          calculator." Collapsed by default so it doesn't dominate the
          workspace; expand-on-demand. */}
      <details className="bg-white border border-slate-200 rounded-xl overflow-hidden">
        <summary className="cursor-pointer px-5 py-3 font-semibold text-slate-900 hover:bg-slate-50 flex items-center gap-2">
          <span>💰</span>
          <span>IRMAA estimator</span>
          <span className="text-xs text-slate-500 ml-2">Part B + Part D surcharges by MAGI · 2025 brackets</span>
        </summary>
        <div className="p-4 bg-slate-50 border-t border-slate-200">
          <IrmaaCalculator/>
        </div>
      </details>
      {/* Medigap Birthday Rule — state-by-state guaranteed-issue
          window lookup. Per audit response §18 Phase 9 Medicare row
          "Birthday rule / LIS copay state tracker." */}
      <details className="bg-white border border-slate-200 rounded-xl overflow-hidden">
        <summary className="cursor-pointer px-5 py-3 font-semibold text-slate-900 hover:bg-slate-50 flex items-center gap-2">
          <span>🎂</span>
          <span>Medigap Birthday Rule</span>
          <span className="text-xs text-slate-500 ml-2">State-by-state GI window lookup + per-client window math</span>
        </summary>
        <div className="p-4 bg-slate-50 border-t border-slate-200">
          <MedigapBirthdayRule/>
        </div>
      </details>
    </div>
  );
}

// ── AEP Command Center ──────────────────────────────────────────────
function AEPCommandCenter({ data, onReload, showToast }) {
  const { aep, kpis } = data;
  const [showCreate, setShowCreate] = React.useState(false);
  const [c, setC] = React.useState({ name: '', campaign_kind: 'aep_renewal_at_risk', message_template: 'Hi {{first_name}} — quick AEP check-in. Want to confirm your current plan is still your best fit?', audience_filter_json: '{"sector":"medicare","renewal_in_days":60}' });

  const createCampaign = async () => {
    if (!c.name.trim() || !c.message_template.trim()) { showToast('Name + message required.'); return; }
    let af = null;
    try { af = c.audience_filter_json ? JSON.parse(c.audience_filter_json) : null; }
    catch { showToast('audience_filter is not valid JSON.'); return; }
    try {
      await apiFetch('/api/medicare/actions', { method: 'POST', body: JSON.stringify({ action: 'campaign_create', ...c, audience_filter: af }) });
      showToast('Campaign drafted.');
      setShowCreate(false); setC({ ...c, name: '', message_template: '' });
      onReload();
    } catch (err) { showToast(`Create failed: ${err.message}`); }
  };

  // markSent records bookkeeping for a campaign you ALREADY sent
  // through your carrier console — it does NOT trigger a live send.
  // Two-step confirm because "rrrr" / "sss" test drafts looked
  // sendable to auditors (BUG-019).
  const markSent = async (id) => {
    const camp = (data.campaigns || []).find(x => x.id === id);
    const label = camp?.name || 'this campaign';
    const ok = window.confirm(
      `Log "${label}" as sent?\n\n` +
      `This is BOOKKEEPING ONLY — it does NOT send any messages. ` +
      `Use this after you've already sent the campaign through your carrier console / SMS tool, so the dashboard counts are accurate.\n\n` +
      `Click OK to log it as sent, or Cancel to keep as a draft.`
    );
    if (!ok) return;
    const n = window.prompt(`How many recipients did "${label}" go out to?`);
    if (n == null) return;
    try {
      await apiFetch('/api/medicare/actions', { method: 'POST', body: JSON.stringify({ action: 'campaign_send', id, sent_count: Number(n) || 0 }) });
      onReload();
    } catch (err) { showToast(`Update failed: ${err.message}`); }
  };

  const phaseLabel = aep.phase === 'pre' ? 'AEP starts' : aep.phase === 'active' ? 'AEP active — closes' : 'Next AEP starts';
  const phaseTone  = aep.phase === 'active' ? 'bg-rose-50 border-rose-300 text-rose-900' :
                     aep.phase === 'pre'    ? 'bg-amber-50 border-amber-300 text-amber-900' :
                                              'bg-slate-50 border-slate-300 text-slate-700';

  return (
    <div className="bg-white border-2 border-blue-900 rounded-xl overflow-hidden">
      <div className="bg-blue-900 text-white px-5 py-3">
        <div className="text-blue-300 text-[0.55rem] font-black uppercase tracking-[0.3em]">Medicare · AEP Command Center</div>
        <div className="text-white text-lg font-bold mt-0.5 flex items-center gap-3 flex-wrap">
          <span>Oct 15 – Dec 7 enrollment window</span>
        </div>
        {/* Shipped-features indicators — per audit response §18 Phase 9
            "ANOC automation," "SOA + 48h rule," "OEP countdown" were
            flagged as missing. All three are shipped via the universal
            workflow engine + migration 092 SOA v2. Surfacing the chips
            here so operators (and auditors) see them at a glance. */}
        <div className="mt-2 flex items-center gap-1.5 flex-wrap">
          <span className="text-[10px] font-bold uppercase tracking-wider bg-emerald-500/20 text-emerald-200 border border-emerald-400/40 rounded px-1.5 py-0.5" title="Auto-trigger review touch at day 270 from policy issue — set per Medicare workflow def 'anoc_alert'">
            ✓ ANOC outreach automated
          </span>
          <span className="text-[10px] font-bold uppercase tracking-wider bg-emerald-500/20 text-emerald-200 border border-emerald-400/40 rounded px-1.5 py-0.5" title="CMS-compliant e-signature flow with 48-hour wait enforcement + tamper-evident audit trail">
            ✓ SOA + 48h rule
          </span>
          <span className="text-[10px] font-bold uppercase tracking-wider bg-emerald-500/20 text-emerald-200 border border-emerald-400/40 rounded px-1.5 py-0.5" title="TPMO disclaimer auto-appended to every Medicare benefit-specific message">
            ✓ TPMO compliance
          </span>
        </div>
      </div>

      {/* Countdown + 4 KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-2 p-3">
        <div className={`border-2 rounded-md p-3 ${phaseTone}`}>
          <div className="text-[10px] font-bold uppercase tracking-wider opacity-75">{phaseLabel}</div>
          <div className="text-2xl font-black tabular-nums mt-0.5">{aep.days} <span className="text-xs">d</span></div>
          <div className="text-[10px] opacity-75 mt-0.5">{aep.phase === 'active' ? `until ${aep.ends_on}` : `on ${aep.starts_on}`}</div>
        </div>
        <KpiBox label="Renewals at risk" value={kpis.renewals_at_risk} tone="rose"/>
        <KpiBox label="AEP window" value={kpis.aep_window_count} tone="amber"/>
        <KpiBox label="T65 cohort" value={kpis.t65_total} tone="violet"/>
        <KpiBox label="Open SEPs" value={kpis.sep_open} sub={`${kpis.sep_expiring_soon} ≤14d`} tone="sky"/>
      </div>

      {/* Bulk campaign launcher */}
      <div className="border-t border-slate-100 px-5 py-3">
        <div className="flex items-center justify-between gap-2 flex-wrap">
          <div className="text-[10px] font-bold uppercase tracking-wider text-slate-700 flex items-center gap-1.5"><Megaphone size={11}/> Bulk campaigns ({(data.campaigns || []).length})</div>
          <button onClick={() => setShowCreate(!showCreate)} className="text-xs font-bold uppercase tracking-wider text-emerald-700 hover:text-emerald-900 inline-flex items-center gap-1"><Plus size={11}/> New campaign</button>
        </div>
        {showCreate && (
          <div className="bg-slate-50 border border-slate-200 rounded-md px-3 py-2 mt-2 space-y-2">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
              <input value={c.name} onChange={e => setC({ ...c, name: e.target.value })} placeholder="Campaign name" className="text-sm border border-slate-300 rounded-md px-2 py-1.5"/>
              <select value={c.campaign_kind} onChange={e => setC({ ...c, campaign_kind: e.target.value })} className="text-sm border border-slate-300 rounded-md px-2 py-1.5 bg-white">
                {CAMPAIGN_KINDS.map(k => <option key={k.id} value={k.id}>{k.label}</option>)}
              </select>
            </div>
            <textarea value={c.message_template} onChange={e => setC({ ...c, message_template: e.target.value })} rows={2} placeholder="Message body. Merge: {{first_name}}, {{plan_name}}, {{renewal_date}}" className="w-full text-xs border border-slate-300 rounded-md px-2 py-1.5"/>
            <input value={c.audience_filter_json} onChange={e => setC({ ...c, audience_filter_json: e.target.value })} placeholder='Audience filter JSON (optional)' className="w-full text-xs font-mono border border-slate-300 rounded-md px-2 py-1.5"/>
            <div className="flex gap-2">
              <button onClick={createCampaign} className="bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-bold px-4 py-1.5 rounded-md">Save draft</button>
              <button onClick={() => setShowCreate(false)} className="text-xs text-slate-600 hover:text-slate-900 px-3 py-1.5">Cancel</button>
            </div>
          </div>
        )}
        {(data.campaigns || []).length > 0 && (
          <div className="mt-2 space-y-1">
            {(data.campaigns || []).slice(0, 8).map(camp => (
              <div key={camp.id} className="flex items-center gap-2 text-xs py-1 border-b border-slate-100 last:border-b-0">
                <span className={`text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded border ${camp.status === 'sent' ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : 'bg-slate-50 text-slate-700 border-slate-200'}`}>{camp.status}</span>
                <span className="font-semibold text-slate-900 flex-1 truncate">{camp.name}</span>
                <span className="text-slate-500">{camp.campaign_kind}</span>
                {camp.sent_count > 0 && <span className="text-slate-700 tabular-nums">{camp.sent_count} sent</span>}
                {camp.status === 'draft' && (
                  <button
                    onClick={() => markSent(camp.id)}
                    title="Bookkeeping only — does not actually send any messages"
                    className="text-[10px] font-bold uppercase tracking-wider text-sky-700 hover:text-sky-900 inline-flex items-center gap-1"
                  >
                    <Send size={10}/> Log as sent
                  </button>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function KpiBox({ label, value, sub, tone }) {
  const toneClass = tone === 'rose' ? 'bg-rose-50 border-rose-200 text-rose-900' :
                    tone === 'amber' ? 'bg-amber-50 border-amber-200 text-amber-900' :
                    tone === 'violet' ? 'bg-violet-50 border-violet-200 text-violet-900' :
                    tone === 'sky'   ? 'bg-sky-50 border-sky-200 text-sky-900' :
                                       'bg-slate-50 border-slate-200 text-slate-700';
  return (
    <div className={`border-2 rounded-md p-3 ${toneClass}`}>
      <div className="text-[10px] font-bold uppercase tracking-wider opacity-75">{label}</div>
      <div className="text-2xl font-black tabular-nums mt-0.5">{(value ?? 0).toLocaleString()}</div>
      {sub && <div className="text-[10px] opacity-75 mt-0.5">{sub}</div>}
    </div>
  );
}

// ── SEP Trigger Engine ──────────────────────────────────────────────
function SEPTriggerEngine({ data, onReload, showToast }) {
  const sep = data.sep || [];
  const [showLog, setShowLog] = React.useState(false);
  const [f, setF] = React.useState({ client_name: '', client_phone: '', event_kind: 'birthday', event_date: '', sep_window_days: 60, notes: '' });

  const log = async () => {
    if (!f.client_name.trim() || !f.event_date) { showToast('Client name + event date required.'); return; }
    try {
      await apiFetch('/api/medicare/actions', { method: 'POST', body: JSON.stringify({ action: 'sep_log', ...f, sep_window_days: Number(f.sep_window_days) || 60 }) });
      showToast('SEP logged + task created.');
      setF({ client_name: '', client_phone: '', event_kind: 'birthday', event_date: '', sep_window_days: 60, notes: '' });
      setShowLog(false);
      onReload();
    } catch (err) { showToast(`Log failed: ${err.message}`); }
  };

  return (
    <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
      <div className="bg-slate-50 px-4 py-2 border-b border-slate-100 flex items-center justify-between">
        <div className="text-[10px] font-bold uppercase tracking-wider text-slate-700 flex items-center gap-1.5"><Calendar size={11}/> SEP Trigger Engine ({sep.length} open)</div>
        <button onClick={() => setShowLog(!showLog)} className="text-xs font-bold uppercase tracking-wider text-emerald-700 hover:text-emerald-900 inline-flex items-center gap-1"><Plus size={11}/> Log event</button>
      </div>
      {showLog && (
        <div className="bg-slate-50 border-b border-slate-200 px-4 py-2 space-y-2">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            <input value={f.client_name} onChange={e => setF({ ...f, client_name: e.target.value })} placeholder="Client name" className="text-sm border border-slate-300 rounded-md px-2 py-1.5"/>
            <input value={f.client_phone} onChange={e => setF({ ...f, client_phone: e.target.value })} placeholder="Phone (optional)" className="text-sm border border-slate-300 rounded-md px-2 py-1.5"/>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
            <select value={f.event_kind} onChange={e => setF({ ...f, event_kind: e.target.value })} className="text-sm border border-slate-300 rounded-md px-2 py-1.5 bg-white">
              {SEP_KINDS.map(k => <option key={k.id} value={k.id}>{k.label}</option>)}
            </select>
            <input type="date" value={f.event_date} onChange={e => setF({ ...f, event_date: e.target.value })} className="text-sm border border-slate-300 rounded-md px-2 py-1.5"/>
            <input type="number" value={f.sep_window_days} onChange={e => setF({ ...f, sep_window_days: e.target.value })} placeholder="Window (days)" className="text-sm border border-slate-300 rounded-md px-2 py-1.5"/>
          </div>
          <input value={f.notes} onChange={e => setF({ ...f, notes: e.target.value })} placeholder="Notes (optional)" className="w-full text-sm border border-slate-300 rounded-md px-2 py-1.5"/>
          <div className="flex gap-2">
            <button onClick={log} className="bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-bold px-4 py-1.5 rounded-md">Log + auto-task</button>
            <button onClick={() => setShowLog(false)} className="text-xs text-slate-600 hover:text-slate-900 px-3 py-1.5">Cancel</button>
          </div>
        </div>
      )}
      {sep.length === 0 ? (
        <div className="px-4 py-4 text-sm text-slate-500 italic">No open SEP events. Log one when a client has a qualifying life event.</div>
      ) : (
        <div className="divide-y divide-slate-100 max-h-72 overflow-y-auto">
          {sep.slice(0, 30).map(s => {
            const tone = s.days_remaining <= 7 ? 'bg-rose-50 text-rose-700 border-rose-200' : s.days_remaining <= 21 ? 'bg-amber-50 text-amber-700 border-amber-200' : 'bg-slate-50 text-slate-700 border-slate-200';
            return (
              <div key={s.id} className="px-4 py-2">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-bold text-slate-900 text-sm">{s.client_name}</span>
                  <span className="text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded bg-slate-100 text-slate-700 border border-slate-200">{String(s.event_kind).replace(/_/g, ' ')}</span>
                  <span className={`text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded border ${tone}`}>{s.days_remaining}d left</span>
                </div>
                <div className="text-xs text-slate-500 mt-0.5">Event {s.event_date} · expires {s.expires_on}</div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ── Renewals at Risk panel ──────────────────────────────────────────
function RenewalsAtRiskPanel({ renewals }) {
  const list = renewals || [];
  return (
    <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
      <div className="bg-slate-50 px-4 py-2 border-b border-slate-100 text-[10px] font-bold uppercase tracking-wider text-slate-700 flex items-center gap-1.5">
        <AlertTriangle size={11} className="text-rose-600"/> Medicare renewals — at risk / AEP window ({list.length})
      </div>
      {list.length === 0 ? (
        <div className="px-4 py-4 text-sm text-slate-500 italic">No urgent Medicare renewals.</div>
      ) : (
        <div className="divide-y divide-slate-100 max-h-72 overflow-y-auto">
          {list.slice(0, 30).map(r => {
            const tone = r.urgency === 'expired' ? 'bg-rose-100 text-rose-800 border-rose-300' : r.urgency === 'critical' ? 'bg-rose-50 text-rose-700 border-rose-200' : r.urgency === 'aep_window' ? 'bg-amber-50 text-amber-700 border-amber-200' : 'bg-slate-50 text-slate-700 border-slate-200';
            return (
              <div key={r.policy_id} className="px-4 py-2">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-bold text-slate-900 text-sm">{r.client_name}</span>
                  <span className={`text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded border ${tone}`}>{r.urgency}</span>
                </div>
                <div className="text-xs text-slate-500 mt-0.5">
                  {r.carrier} · {r.plan_name} · {r.days_until_expiration < 0 ? `expired ${Math.abs(r.days_until_expiration)}d ago` : `${r.days_until_expiration}d until ${r.next_expiration_date}`}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ── T65 Pipeline (Kanban-style by stage) ────────────────────────────
function T65PipelinePanel({ t65, onReload, showToast }) {
  const byStage = T65_STAGES.reduce((m, s) => { m[s.id] = []; return m; }, {});
  for (const r of (t65 || [])) {
    (byStage[r.stage] || byStage.cold_start).push(r);
  }

  const setStage = async (clientId, stage) => {
    try {
      await apiFetch('/api/medicare/actions', { method: 'POST', body: JSON.stringify({ action: 't65_set_stage', client_id: clientId, stage }) });
      onReload();
    } catch (err) { showToast(`Update failed: ${err.message}`); }
  };

  if ((t65 || []).length === 0) {
    return (
      <div className="bg-white border border-slate-200 rounded-xl p-5">
        <div className="text-[10px] font-bold uppercase tracking-wider text-slate-700 flex items-center gap-1.5"><Calendar size={11}/> T65 Pipeline</div>
        <div className="text-sm text-slate-500 italic mt-2">No clients currently in the T65 window (DOB between 64.0 and 65.5 years). Add clients with DOBs to populate.</div>
      </div>
    );
  }

  return (
    <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
      <div className="bg-slate-50 px-4 py-2 border-b border-slate-100 text-[10px] font-bold uppercase tracking-wider text-slate-700 flex items-center gap-1.5">
        <Calendar size={11}/> T65 Pipeline ({(t65 || []).length} in window)
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-2 p-3">
        {T65_STAGES.map(s => (
          <div key={s.id} className={`border-2 ${s.tone} rounded-lg p-2 min-h-[180px]`}>
            <div className="text-[10px] font-bold uppercase tracking-wider text-slate-700 px-1 pb-2 flex items-center justify-between">
              {s.label}
              <span className="tabular-nums text-slate-500">{byStage[s.id].length}</span>
            </div>
            <div className="space-y-1.5">
              {byStage[s.id].slice(0, 30).map(c => (
                <div key={c.client_id} className="bg-white border border-slate-200 rounded-md px-2 py-1.5 text-xs">
                  <div className="font-bold text-slate-900 truncate">{c.client_name}</div>
                  <div className="text-[10px] text-slate-500">
                    {c.days_until_65 > 0 ? `${c.days_until_65}d to 65` : `${Math.abs(c.days_until_65)}d past 65`}
                  </div>
                  <select value={c.stage} onChange={e => setStage(c.client_id, e.target.value)} className="mt-1 w-full text-[10px] border border-slate-200 rounded px-1 py-0.5 bg-slate-50">
                    {T65_STAGES.map(opt => <option key={opt.id} value={opt.id}>{opt.label}</option>)}
                    <option value="lost">Lost</option>
                  </select>
                </div>
              ))}
              {byStage[s.id].length === 0 && (
                <div className="text-[10px] text-slate-400 italic text-center py-2">empty</div>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
